'use client';

import { consoleLog, isLocalDevelopment, maybeReloadOnChunkError } from '@/utils';
import { ErrorState } from '@/components/ErrorState';
import { FEATURE_FLAGS } from '@/constants';
import { getApplicationInsights } from '@/lib/application-insights';
import { useEffect } from 'react';

/**
 * Root segment error boundary.
 *
 * Catches render/lifecycle/effect errors thrown anywhere below the root layout
 * and replaces the crashed subtree with a branded, recoverable fallback while
 * keeping the app shell (providers/layout) mounted.
 *
 * Telemetry note:
 * - Reporting here is additive to the global `error`/`unhandledrejection`
 *   listeners in the AppInsights component, because React errors caught by an
 *   error boundary never reach `window.onerror` — so there is no duplicate event.
 * - Only reports in deployed environments (skipped on localhost dev).
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Post-deploy chunk-load failures are recoverable: reload once onto the
    // current build before falling back to the manual error UI below.
    if (maybeReloadOnChunkError(error)) return;

    consoleLog.error('App error boundary caught an error', error);

    if (isLocalDevelopment()) return;
    if (!FEATURE_FLAGS.ENABLE_APP_INSIGHTS) return;

    getApplicationInsights()?.trackException({
      exception: error,
      properties: { digest: error.digest, boundary: 'app/error' },
    });
  }, [error]);

  return (
    <ErrorState
      digest={error.digest}
      onRetry={reset}
      secondaryLabel="Return to home"
      onSecondary={() => {
        window.location.href = '/';
      }}
    />
  );
}
